const VERDICT_LABELS = {
  paid_overrun: "Já estourou",
  committed_overrun: "Estouro comprometido",
  within_budget: "Dentro do orçamento",
  unavailable: "Fechamento indisponível",
};

const MEASURED_SOURCE_LABELS = {
  prevision: "Prevision",
  sienge: "Sienge",
  none: "Indisponível",
};

const safeCurrency = (cents) => {
  if (!Number.isSafeInteger(cents)) return "Indisponível";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(cents / 100)
    .replace(/\u00a0/g, " ");
};

const safePercent = (basisPoints) => {
  if (!Number.isSafeInteger(basisPoints)) return "Indisponível";
  return `${new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(basisPoints) / 100)}%`;
};

const safeDate = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return "Data indisponível";
  }
  const parsed = new Date(`${value}T12:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return "Data indisponível";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeZone: "UTC",
  }).format(parsed);
};

const safeTimestamp = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Momento indisponível";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Momento indisponível";
  return `Atualizado em ${new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(parsed)}`;
};

const metric = (label, value) => (
  <div key={label}>
    <dt>{label}</dt>
    <dd>{safeCurrency(value)}</dd>
  </div>
);

const verdictState = (verdict, sourceState) => {
  if (sourceState?.financialStatus === "mixed_generation") {
    return {
      status: "unavailable",
      label: VERDICT_LABELS.unavailable,
      detail:
        "Há gerações financeiras diferentes neste recorte. O fechamento foi bloqueado para não misturar snapshots.",
    };
  }
  const status = VERDICT_LABELS[verdict?.status]
    ? verdict.status
    : "unavailable";
  const detail = {
    paid_overrun:
      "O valor pago já ultrapassou o orçamento deste grupo financeiro.",
    committed_overrun:
      "O pago ainda não estourou, mas o valor comprometido já ultrapassou o orçamento.",
    within_budget:
      "O valor financeiro usado no veredito permanece dentro do orçamento.",
    unavailable:
      "Não há um snapshot financeiro compatível para fechar este grupo com segurança.",
  }[status];
  return { status, label: VERDICT_LABELS[status], detail };
};

const BridgeItem = ({ value, children }) => (
  <li>
    <strong>{safeCurrency(value)}</strong> {children}
  </li>
);

const relationLabel = (value, { positive, negative, neutral }) => {
  if (!Number.isSafeInteger(value)) return "relação indisponível";
  if (value > 0) return positive;
  if (value < 0) return negative;
  return neutral;
};

const Reconciliation = ({ values }) => {
  const resourceBudgetLabel = relationLabel(
    values?.resourceAppropriatedVsBudgetCents,
    {
      positive: "de apropriação acima do orçamento",
      negative: "de apropriação abaixo do orçamento",
      neutral: "de apropriação alinhada ao orçamento",
    },
  );
  const paidAppropriationLabel = relationLabel(
    values?.paidVsResourceAppropriationCents,
    {
      positive: "de pago além do apropriado por insumos",
      negative: "de apropriado por insumos ainda não pago",
      neutral: "de pago alinhado ao apropriado por insumos",
    },
  );

  return <>
    <dl className="resource-group-financial__metrics">
      {metric("Orçado", values?.budgetCents)}
      {metric("Apropriado por insumos", values?.resourceAppropriatedCents)}
      {metric("Medido", values?.measuredCents)}
      {metric("Pago", values?.paidCents)}
      {metric("Comprometido", values?.committedCents)}
    </dl>
    <div className="resource-group-financial__analysis">
      <div
        className="resource-group-financial__bridge"
        aria-label="Ponte aditiva do estouro"
      >
        <h5>Ponte do orçamento ao pago</h5>
        <ul>
          <BridgeItem value={values?.resourceAppropriatedVsBudgetCents}>
            {resourceBudgetLabel}
          </BridgeItem>
          <BridgeItem value={values?.paidVsResourceAppropriationCents}>
            {paidAppropriationLabel}
          </BridgeItem>
          <BridgeItem value={values?.paidVsBudgetCents}>
            de desvio total do Pago frente ao orçamento
          </BridgeItem>
        </ul>
        <small>
          Esta ponte é aditiva: orçamento, apropriação por insumos e pagamento
          chegam ao desvio total sem somar comparadores paralelos.
        </small>
      </div>
      <div
        className="resource-group-financial__bridge"
        aria-label="Comparador de timing"
      >
        <h5>Caixa versus avanço físico</h5>
        <ul>
          <BridgeItem value={values?.paidVsMeasuredCents}>
            de diferença entre o Pago e o Medido
          </BridgeItem>
        </ul>
        <small>
          Este valor compara momentos de caixa e execução física. Ele não compõe
          novamente o estouro e não prova vínculo entre título e insumo.
        </small>
      </div>
      <div
        className="resource-group-financial__bridge"
        aria-label="Composição do pago por origem"
      >
        <h5>Composição do pago por origem</h5>
        <ul>
          <BridgeItem value={values?.supplyOriginPaidCents}>
            pagos com origem de suprimentos
          </BridgeItem>
          <BridgeItem value={values?.directFinancePaidCents}>
            pagos com origem financeira direta
          </BridgeItem>
          {Number.isSafeInteger(values?.unclassifiedOriginPaidCents)
            && values.unclassifiedOriginPaidCents !== 0 ? (
              <BridgeItem value={values.unclassifiedOriginPaidCents}>
                pagos sem origem classificada
              </BridgeItem>
            ) : null}
        </ul>
        <small>
          Esta divisão reparte o Pago por origem; ela não adiciona um novo desvio
          ao fechamento.
        </small>
      </div>
    </div>
  </>;
};

const ResourceCoverage = ({ coverage }) => {
  if (!coverage) return null;
  return (
    <aside
      className="resource-group-financial__coverage"
      aria-label="Cobertura dos insumos do grupo"
    >
      <strong>
        {coverage.comparableItems} de {coverage.totalItems} insumos com
        comparação completa
      </strong>
      <span>
        {coverage.actualOnlyItems} insumos realizados sem orçamento comparável
      </span>
      <span>{safeCurrency(coverage.actualOnlyCents)} sem base comparável</span>
      <span>
        {coverage.budgetOnlyItems} insumos orçados sem realizado comparável
      </span>
      <span>{safeCurrency(coverage.budgetOnlyCents)} sem realizado comparável</span>
    </aside>
  );
};

const TitleRow = ({ title, workId, buildTitleHref }) => {
  const href =
    typeof buildTitleHref === "function"
      ? buildTitleHref({ workId, titleId: title.titleId, state: title.state })
      : null;
  return (
    <li>
      <div>
        <strong>Título {title.titleId}</strong>
        <span>
          {title.documentType || "Documento não informado"} · {safeDate(title.date)}
        </span>
        <small>
          Origem {title.origin || "não classificada"} · {safeCurrency(title.totalCents)}
        </small>
      </div>
      {href ? <a href={href}>Abrir título {title.titleId}</a> : null}
    </li>
  );
};

const SupplierDrivers = ({ suppliers, titles, workId, buildTitleHref }) => {
  const safeSuppliers = Array.isArray(suppliers) ? suppliers : [];
  const safeTitles = Array.isArray(titles) ? titles : [];
  return (
    <section
      className="resource-group-financial__suppliers"
      aria-labelledby="resource-group-financial-suppliers-title"
    >
      <header>
        <div>
          <span className="item-ai-eyebrow">Rastreabilidade financeira</span>
          <h5 id="resource-group-financial-suppliers-title">
            Fornecedores mais representativos
          </h5>
        </div>
        <small>Abra um fornecedor para conferir os títulos.</small>
      </header>
      {safeSuppliers.length === 0 ? (
        <p>Não há fornecedores representativos disponíveis neste snapshot.</p>
      ) : (
        <div className="resource-group-financial__supplier-list">
          {safeSuppliers.map((supplier) => {
            const refs = new Set(supplier.topTitleRefs ?? []);
            const supplierTitles = safeTitles.filter(
              (title) =>
                title.supplierRef === supplier.supplierRef &&
                (refs.size === 0 || refs.has(title.titleRef)),
            );
            return (
              <details key={supplier.supplierRef}>
                <summary>
                  <span>{supplier.name}</span>
                  <strong>{safeCurrency(supplier.totalTitleCents)}</strong>
                  <small>
                    {safePercent(supplier.shareBps)} dos títulos financeiros · {supplier.titleCount}{" "}
                    {supplier.titleCount === 1 ? "título" : "títulos"}
                  </small>
                </summary>
                {supplierTitles.length > 0 ? (
                  <ul>
                    {supplierTitles.map((title) => (
                      <TitleRow
                        key={title.titleRef}
                        title={title}
                        workId={workId}
                        buildTitleHref={buildTitleHref}
                      />
                    ))}
                  </ul>
                ) : (
                  <p>Os números dos títulos deste fornecedor não estão disponíveis.</p>
                )}
              </details>
            );
          })}
        </div>
      )}
    </section>
  );
};

const SourceState = ({ state }) => {
  const financialTimestamp = safeTimestamp(state?.financialSynchronizedAt);
  const financialMoment = financialTimestamp === "Momento indisponível"
    ? "momento indisponível"
    : financialTimestamp.replace(/^Atualizado/, "atualizado");
  return (
  <footer className="resource-group-financial__sources">
    <div>
      <strong>
        Medido: {MEASURED_SOURCE_LABELS[state?.measuredSource] ?? "Indisponível"}
      </strong>
      <small>{safeTimestamp(state?.measuredSynchronizedAt)}</small>
    </div>
    <div>
      <span>
        Financeiro: {state?.financialStatus === "available" ? "sincronizado" : "indisponível"}
      </span>
      <small>
        {Number.isSafeInteger(state?.financialMeasurementNumber)
          ? `Medição ${state.financialMeasurementNumber} · ${financialMoment}`
          : financialMoment}
      </small>
    </div>
    <div>
      <span>
        Insumos: {state?.resourceStatus === "available" ? "disponíveis" : "indisponíveis"}
      </span>
      <small>{safeTimestamp(state?.resourceSynchronizedAt)}</small>
    </div>
    <div>
      <span>
        Títulos: {state?.titleStatus === "available" ? "disponíveis" : "indisponíveis"}
      </span>
    </div>
  </footer>
  );
};

export default function ResourceGroupFinancialSummary({
  workId,
  verdict,
  reconciliation,
  resourceCoverage,
  financialTitles,
  sourceState,
  buildTitleHref,
}) {
  const resolvedVerdict = verdictState(verdict, sourceState);
  const percent = safePercent(verdict?.varianceBps);
  const percentLabel = verdict?.budgetCents === 0 && verdict?.varianceBps === null
    && resolvedVerdict.status !== "unavailable"
    ? "Percentual indefinido: orçamento igual a zero"
    : resolvedVerdict.status === "within_budget"
      ? `${percent} de variação frente ao orçamento`
      : resolvedVerdict.status === "unavailable"
        ? "Percentual indisponível"
        : `${percent} acima do orçamento`;

  return (
    <section
      className={`resource-group-financial resource-group-financial--${resolvedVerdict.status}`}
      aria-labelledby="resource-group-financial-title"
    >
      <header className="resource-group-financial__headline">
        <span className="item-ai-eyebrow">Fechamento financeiro</span>
        <h4 id="resource-group-financial-title">{resolvedVerdict.label}</h4>
        <strong>{safeCurrency(verdict?.varianceCents)}</strong>
        <small>{percentLabel}</small>
        <p>{resolvedVerdict.detail}</p>
      </header>
      <Reconciliation values={reconciliation} />
      <ResourceCoverage coverage={resourceCoverage} />
      <SupplierDrivers
        suppliers={financialTitles?.topSuppliers}
        titles={financialTitles?.topTitles}
        workId={workId}
        buildTitleHref={buildTitleHref}
      />
      <SourceState state={sourceState} />
    </section>
  );
}
